import { MatDialog } from '@angular/material/dialog'
import { KeyValue } from '@app/core/types/key-value.type'
import { UploadModalConfig, UploadType } from '@app/shared/components/upload-modal/models/upload-modal-config'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { LibraryCascadeChangesModalComponent } from '@app/features/library-management/components/modals'
import { LibraryEntriesDuplicateMapRatecardsModalComponent } from '@app/features/library-management/components/modals/library-entries-duplicate-map-ratecards-modal/library-entries-duplicate-map-ratecards-modal.component'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { EMPTY, filter, forkJoin, map, switchMap, tap, throwError } from 'rxjs'
import * as XLSX from 'xlsx'
import { LanguageService } from '@app/core/service/language.service'
import { sortBy } from 'lodash'
import { Injectable } from '@angular/core'

export enum FileErrorTypeEnum {
  FILE_NOT_CORRECT = 'FILE_NOT_CORRECT',
  IMPORT_FAILED = 'IMPORT_FAILED',
  SECOND_PARTY_NOT_MATCH = 'SECOND_PARTY_NOT_MATCH',
  SEE_ERRORS = 'SEE_ERRORS',
  DEFAULT_ERROR = 'DEFAULT_ERROR',
}

@Injectable()
export class UploadModalService {
  uploading = false

  file: File
  fileName: string
  fileUrl: string

  errors: any[] = []
  errorText: KeyValue<string>
  showErrorList = false

  modalConfig: UploadModalConfig

  selectedRateCard: any

  private _showGenerateTemplate: boolean
  get showGenerateTemplate(): boolean {
    return this._showGenerateTemplate
  }
  set showGenerateTemplate(value: boolean) {
    this._showGenerateTemplate = value
  }

  constructor(
    private manageService: CompanyManagementService,
    private lang: LanguageService,
    private dialog: MatDialog
  ) {}

  setModalConfig(data: UploadModalConfig) {
    this.modalConfig = data
    this.errorText = {
      [FileErrorTypeEnum.FILE_NOT_CORRECT]: `File is not valid. Please use ${this.modalConfig.type === UploadType.WORD_OUTPUT ? '.docx' : '.xlsx'}`,
      [FileErrorTypeEnum.IMPORT_FAILED]: 'Error while uploading document, please try again',
      [FileErrorTypeEnum.SECOND_PARTY_NOT_MATCH]: 'Second Party name missing',
      [FileErrorTypeEnum.SEE_ERRORS]: 'Error while uploading document, please see errors',
      [FileErrorTypeEnum.DEFAULT_ERROR]: 'Value is not correct',
    }
  }

  reset() {
    this.modalConfig = null
    this.file = null
    this.fileName = null
    this.fileUrl = null
    this.errors = []
    this.errorText = null
    this.showErrorList = false
    this.selectedRateCard = null
    this.uploading = false
  }

  uploadFile($event: any) {
    this.file = $event.target.files[0]
    this.fileName = $event.target.files[0].name
    this.errors = []
  }

  submit() {
    if (this.file && !this.uploading) {
      const fileExt = this.fileName.split('.').pop().toLowerCase()
      if (
        (this.modalConfig.type !== UploadType.WORD_OUTPUT && fileExt.toLowerCase() === 'xlsx') ||
        (this.modalConfig.type === UploadType.WORD_OUTPUT && fileExt.toLowerCase() === 'docx')
      ) {
        this.uploading = true
        this.modalConfig.submitFn(this.file).subscribe({
          next: (result) => {
            if (this.modalConfig.type === UploadType.RATECARD) {
              this.showCascadeChangesModal(result)
            } else {
              this.modalConfig.successFn(result)
            }
          },
          error: (error) => {
            if (error.status == 409) {
              this.showErrorList = true
              this.errors.push({ errorType: FileErrorTypeEnum.SEE_ERRORS })
              const blob = new Blob([error.error], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              })
              const originalFile = this.file.name
              let verifiedFileName = 'Verified_'
              const dateFormat = new Date()
                .toISOString()
                .slice(0, 16)
                .replace('T', '_')
                .replace(':', '')
                .replaceAll('-', '')
              verifiedFileName = verifiedFileName.concat(dateFormat, '-', originalFile)
              this.fileName = verifiedFileName
              this.fileUrl = window.URL.createObjectURL(blob)
            }
            this.errors.push({ errorType: FileErrorTypeEnum.IMPORT_FAILED })
            this.uploading = false
            return throwError(error)
          },
        })
      } else {
        this.errors.push({ errorType: FileErrorTypeEnum.FILE_NOT_CORRECT })
      }
    }
  }

  getTemplateExampleUrl() {
    let headerRow
    let rateCardRow
    switch (this.modalConfig.type) {
      case UploadType.CLIENTS:
        headerRow = [
          `${this.lang.get('second_party')} name`,
          'Rate card',
          'Relationship Since',
          'Output Template',
          'FTE',
        ]
        this.generateTemplate([headerRow], 'upload-clients.xlsx')
        break
      case UploadType.CONTACT:
        headerRow = ['Contact Name', `${this.lang.get('second_party')} Name`, 'Job Title', 'Phone', 'Location', 'Email']
        this.generateTemplate([headerRow], 'upload-contacts.xlsx')
        break
      case UploadType.LOCATION:
        headerRow = [
          'Address Name',
          'Address 1',
          'Address 2',
          'City',
          'Post Code',
          'Country',
          'Phone',
          `${this.lang.get('second_party')} Name`,
        ]
        this.generateTemplate([headerRow], 'upload-locations.xlsx')
        break
      case UploadType.BRAND:
        headerRow = [
          'Brand Name',
          `${this.lang.get('second_party')} Name`,
          'Rate card',
          'Location',
          'Key Contact',
          'Output Template',
          'Relationship Since',
        ]
        this.generateTemplate([headerRow], 'upload-brands.xlsx')
        break
      case UploadType.RATECARD:
        headerRow = ['Department Name', `${this.lang.get('agency')} Role`, 'Location', 'Rate', 'Cost']
        rateCardRow = ['Rate-card Name', '', 'Currency', '', 'Scope Mark Card', 'N']
        this.manageService.getRateCardMetaStructure().subscribe({
          next: (result: any) => {
            if (result && result.ratecardMetaStructureDefinition) {
              result.ratecardMetaStructureDefinition.roles.additionalFields.forEach((role) => {
                headerRow.push(role.name)
              })
            }
            this.generateTemplate([rateCardRow, headerRow], 'upload-ratecard.xlsx')
          },
          error: () => {
            console.error('Failed to load the company rate card meta structure.')
          },
        })
        break
      case UploadType.DELIVERABLE:
        headerRow = [
          `${this.lang.get('deliverable')} Name`,
          'Discipline',
          `${this.lang.get('deliverable')} description`,
          `${this.lang.get('deliverable')} internal note`,
          'Rate-card Name',
        ]
        this.generateTemplate([headerRow], 'upload-deliverables-template.xlsx')
        break
      case UploadType.FIXED_FEE_DELIVERABLE:
        headerRow = [
          `${this.lang.get('deliverable')} Name`,
          'Discipline',
          `${this.lang.get('deliverable')} description`,
          `${this.lang.get('deliverable')} internal note`,
          'Rate-card Name',
          'Fixed Fee Price',
          'Fixed Fee Price Editable (Y/N)',
        ]
        this.generateTemplate([headerRow], 'upload-fixed-fee-deliverables-template.xlsx')
        break
      case UploadType.COMPONENT:
        rateCardRow = ['Rate-card Name']
        headerRow = ['Name', 'Discipline', 'Description', 'Internal note', 'Complexity']
        this.generateTemplate([rateCardRow, headerRow], 'upload-components-template.xlsx')
        break
      case UploadType.FIXED_FEE_COMPONENT:
        rateCardRow = ['Rate-card Name']
        headerRow = [
          'Fixed Fee Component Name',
          'Discipline',
          'Description',
          'Internal note',
          'Complexity (S-XL)',
          this.modalConfig.includeRoleAndHour ? '' : 'Fixed Fee Cost',
          'Fixed Fee Price',
          'Fixed Fee Price Editable (Y/N)',
        ].filter((r) => r.length)
        this.generateTemplate(
          [rateCardRow, headerRow],
          `upload-fixed-fee-${this.modalConfig.includeRoleAndHour ? '' : 'no-roles-and-hours-'}components-template.xlsx`
        )
        break
      case UploadType.TASK:
        rateCardRow = ['Rate-card Name', 'Rate Card 1']
        headerRow = ['Task Name', 'Task description', 'Department 1', '', '', '', 'Department 2', '', '', '']
        const rolesRow = ['', '', '', '', 'Role 1', 'Role 2', 'Role 3', '', 'Role 4', 'Role 5', 'Role 6']
        const valuesRow = ['Task 1', 'My description', '', '1', '2', '3', '', '4', '', '']
        this.generateTemplate(
          [rateCardRow, headerRow, rolesRow, valuesRow],
          'generic-upload-tasks-template.xlsx',
          'Rate Card 1'
        )
        break
      case UploadType.DELIVERABLE_WITH_COMPONENTS:
        headerRow = [
          'Deliverable Name',
          `Discipline`,
          'Deliverable description',
          'Deliverable extra description',
          'Deliverable internal note',
          'Rate-card Name',
          'Component Name',
          'Component Complexity',
          'Component Quantity',
        ]
        this.generateTemplate([headerRow], 'duplicate-deliverables.xlsx')
        break
    }
  }
  generateRateCardTemplate() {
    if (!this.selectedRateCard) return
    this.manageService.getRateCard(this.selectedRateCard.rateCardIdentity.id).subscribe({
      next: (rc: any) => {
        let sheetName = rc.name
        if (rc.name.length > 31) {
          sheetName = rc.name.substring(0, 28) + '...'
        }
        const rateCardRow = ['Rate-card Name', rc.name]
        let headerRow
        let rolesRow
        let fileName

        switch (this.modalConfig.type) {
          case UploadType.COMPONENT:
            headerRow = ['Name', 'Discipline', 'Description', 'Internal note', 'Complexity']
            rolesRow = ['', '', '', '', '']
            fileName = `${rc.name}-upload-components-template.xlsx`
            break
          case UploadType.FIXED_FEE_COMPONENT:
            headerRow = [
              'Fixed Fee Component Name',
              'Discipline',
              'Description',
              'Internal note',
              'Complexity (S-XL)',
              this.modalConfig.includeRoleAndHour ? '' : 'Fixed Fee Cost',
              'Fixed Fee Price',
              'Fixed Fee Price Editable (Y/N)',
            ].filter((r) => r.length)
            fileName = `${rc.name}-upload-fixed-fee-${this.modalConfig.includeRoleAndHour ? '' : 'no-roles-and-hours-'}component-template.xlsx`
            if (this.modalConfig.includeRoleAndHour) rolesRow = ['', '', '', '', '', '', '']
            break
          case UploadType.TASK:
            headerRow = ['Task Name', 'Task description']
            rolesRow = ['', '']
            fileName = `${rc.name}-upload-tasks-template.xlsx`
            break
        }

        if (rolesRow) {
          sortBy(rc.departments, 'name').forEach((dep) => {
            rolesRow.push(``)
            headerRow.push(`${dep.name}`)
            sortBy(dep.roles, 'order').forEach((role) => {
              headerRow.push(``)
              rolesRow.push(`${role.name}`)
            })
          })
        }

        this.generateTemplate([rateCardRow, headerRow, rolesRow], fileName, sheetName)
      },
    })
  }

  private generateTemplate(rows: string[][], name: string, sheetName?: string) {
    const ws = XLSX.utils.aoa_to_sheet(rows)

    ;/!* add to workbook *!/
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, sheetName)
    ;/!* write workbook and force a download *!/
    XLSX.writeFile(wb, name)
  }

  private showCascadeChangesModal(result: any) {
    const initialRatecards = this.modalConfig.ratecards
    this.manageService
      .getAllRateCards()
      .pipe(
        map((updatedRatecards) => this.findFirstDiffByNameAndVersion(initialRatecards, updatedRatecards)),
        tap((diff) => (!!diff && diff.newVersion !== diff.oldVersion ? {} : this.modalConfig.successFn(result))),
        filter((diff) => !!diff && diff.newVersion !== diff.oldVersion)
      )
      .subscribe((diff) => {
        if (diff) {
          this.dialog.open(LibraryCascadeChangesModalComponent, {
            data: {
              itemType: 'ratecard',
              description: 'The items will be updated if ratecard version is increased. Mapping may be required.',
              onSubmit: (result: boolean) => {
                if (result) {
                  forkJoin({
                    source: this.manageService.getRateCard(diff.oldRatecard.rateCardIdentity.id, diff.oldVersion),
                    target: this.manageService.getRateCard(diff.newRatecard.rateCardIdentity.id, diff.newVersion),
                  })
                    .pipe(
                      switchMap(({ source, target }) => {
                        const mappingResult = this.buildMapping(source, target)
                        if (!mappingResult.isNeededToMap) {
                          this.modalConfig.successFn(result)
                          return this.manageService.cascadeRatecardChangesToLibraryItemsAndTasks(
                            source,
                            target,
                            mappingResult.mapping
                          )
                        } else {
                          this.dialog.open(LibraryEntriesDuplicateMapRatecardsModalComponent, {
                            data: {
                              sourceRateCard: source,
                              targetRateCard: target,
                              mappingRoles: mappingResult.mappingRoles,
                              targetRoles: mappingResult.targetRoles,
                              onSubmit: (finalMapping: Record<string, string | null>) => {
                                this.modalConfig.successFn(result)
                                this.manageService
                                  .cascadeRatecardChangesToLibraryItemsAndTasks(source, target, finalMapping)
                                  .subscribe()
                              },
                            },
                          })
                          return EMPTY
                        }
                      })
                    )
                    .subscribe()
                } else {
                  this.modalConfig.successFn(result)
                }
              },
            },
          })
        }
      })
  }

  private buildMapping(
    sourceRatecard: any,
    targetRatecard: any
  ): { mapping: Record<string, string | null>; isNeededToMap: boolean; mappingRoles: any[]; targetRoles: any[] } {
    const sourceDepartments = sourceRatecard?.departments
    const targetDepartments = targetRatecard?.departments
    if (!sourceDepartments || !targetDepartments) {
      return { mapping: {}, isNeededToMap: false, mappingRoles: [], targetRoles: [] }
    }

    const targetDepartmentsRolesByName: Record<string, any> = {}
    const targetRoles: any[] = []
    targetDepartments.forEach((td) =>
      td.roles.forEach((tr) => {
        targetDepartmentsRolesByName[tr.name.toLowerCase()] = tr
        targetRoles.push(tr)
      })
    )

    const distinctRolesMap: Record<string, any> = {}
    let isNeededToMap = false

    sourceDepartments.forEach((sd) =>
      sd.roles.forEach((role) => {
        const key = role.name.toLowerCase()
        if (!distinctRolesMap[key]) {
          const targetRole = targetDepartmentsRolesByName[key] ?? null
          if (!targetRole) {
            isNeededToMap = true
          }
          distinctRolesMap[key] = { id: role.id, name: role.name, targetMappingRole: targetRole, rate: role.rate }
        }
      })
    )

    const mappingRoles = Object.values(distinctRolesMap)
    const mapping = mappingRoles.reduce(
      (acc, mappingRole) => {
        acc[mappingRole.id] = mappingRole.targetMappingRole?.id ?? null
        return acc
      },
      {} as Record<string, string | null>
    )

    return { mapping, isNeededToMap, mappingRoles, targetRoles }
  }

  private findFirstDiffByNameAndVersion(a: RatecardVersion[], b: RatecardVersion[]) {
    const oldRt = a.find((obj) => {
      const match = b.find((newRt) => newRt.name === obj.name)
      return match && match.version !== obj.version
    })
    if (!oldRt) return null
    const match = b.find((newRt) => newRt.name === oldRt.name)!
    return {
      name: oldRt.name,
      oldVersion: oldRt.version,
      newVersion: match.version,
      oldRatecard: oldRt,
      newRatecard: match,
    }
  }
}
